import PreviewMedia from '../../widgets/components/PreviewMedia';
import { indexAfterRemoval } from '../../shared/utils/media';
import {
    Box,
    Button,
    Typography,
    TextField,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    IconButton,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSwipeable } from 'react-swipeable';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import BrandModelSelector from '../../widgets/components/BrandModelSelector';

const radiusOptions = [17, 18, 19, 20, 21, 22, 23];
const typeOptions = ['Кованный', 'Литой'];

const CreateWheelPage = () => {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        diameter: '',
        et: '',
        dia: '',
        pcd: '',
        name: '',
        type: '',
        modelIds: [] as string[],
        price: '',
        isMy: false,
        bestseller: false
    });
    const [errors, setErrors] = useState<{ name?: string }>({});
    const [previews, setPreviews] = useState<string[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [files, setFiles] = useState<File[]>([]);
    const [previewIndex, setPreviewIndex] = useState(0);

    const handleChange = (field: string, value: string | boolean) => {
        if (field === 'pcd' && typeof value === 'string') {
            const digits = value.replace(/[^\d]/g, '');
            if (digits.length >= 2) {
                const pcdValue = `${digits[0]}x${digits.slice(1, 4)}`;
                setForm(prev => ({ ...prev, pcd: pcdValue }));
                return;
            }
        }
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files);
            const newPreviews = newFiles.map(file => URL.createObjectURL(file));
            setFiles(prev => [...prev, ...newFiles]);
            setPreviews(prev => [...prev, ...newPreviews]);
        }
    };

    const handleRemoveFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
        setPreviews(prev => prev.filter((_, i) => i !== index));
        setPreviewIndex(current => indexAfterRemoval(current, index, previews.length));
        setSelectedIndex(current => indexAfterRemoval(current, index, previews.length));
    };

    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => {
            if (selectedIndex < previews.length - 1) {
                setSelectedIndex(selectedIndex + 1);
            }
        },
        onSwipedRight: () => {
            if (selectedIndex > 0) {
                setSelectedIndex(selectedIndex - 1);
            }
        },
        trackMouse: true,
    });

    const handleSubmit = async () => {
        if (!form.name.trim()) {
            setErrors({ name: 'Наименование обязательно' });
            return;
        }

        const formData = new FormData();

        Object.entries(form).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                if (key !== 'modelIds') {
                    value.forEach(v => formData.append(key, v));
                }
            } else {
                formData.append(key, String(value));
            }
        });

        form.modelIds.forEach(id => formData.append('modelIds', id));

        formData.append('previewIndex', String(previewIndex));

        files.forEach(file => formData.append('media', file));

        try {
            await api.post('/api/admin/wheels', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            navigate('/wheels');
        } catch (err) {
            console.error('Ошибка при создании диска:', err);
        }
    };

    return (
        <Box p={4}>
            <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mb: 2 }}>
                ← Назад
            </Button>
            <Typography variant="h5" gutterBottom>
                Создание нового диска
            </Typography>

            <Box component="form" sx={{ display: 'grid', gap: 2 }}>
                <FormControl fullWidth>
                    <InputLabel>Радиус</InputLabel>
                    <Select
                        value={form.diameter}
                        label="Радиус"
                        onChange={e => handleChange('diameter', e.target.value)}
                    >
                        {radiusOptions.map(r => (
                            <MenuItem key={r} value={r}>{`R${r}`}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    label="Вылет (ET)"
                    value={form.et}
                    onChange={e => handleChange('et', e.target.value)}
                    fullWidth
                />

                <TextField
                    label="DIA (центральное отверстие)"
                    value={form.dia}
                    onChange={e => handleChange('dia', e.target.value)}
                    fullWidth
                />

                <TextField
                    label="PCD (напр. 5x112)"
                    value={form.pcd}
                    onChange={e => handleChange('pcd', e.target.value)}
                    fullWidth
                />

                <TextField
                    label="Наименование"
                    value={form.name}
                    onChange={e => {
                        setErrors({});
                        handleChange('name', e.target.value);
                    }}
                    error={Boolean(errors.name)}
                    helperText={errors.name}
                    fullWidth
                    required
                />

                <TextField
                    label="Цена"
                    type="number"
                    value={form.price}
                    onChange={e => handleChange('price', e.target.value)}
                    fullWidth
                />

                <FormControl fullWidth>
                    <InputLabel>Тип диска</InputLabel>
                    <Select
                        value={form.type}
                        label="Тип диска"
                        onChange={e => handleChange('type', e.target.value)}
                    >
                        {typeOptions.map(type => (
                            <MenuItem key={type} value={type}>
                                {type}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={form.isMy}
                            onChange={(e) => handleChange('isMy', e.target.checked)}
                        />
                    }
                    label="Мой диск"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={form.bestseller}
                            onChange={(e) => handleChange('bestseller', e.target.checked)}
                        />
                    }
                    label="Хит продаж"
                />

                <BrandModelSelector
                    value={form.modelIds}
                    onChange={(modelIds) => setForm(prev => ({ ...prev, modelIds }))}
                />

                <Button variant="contained" component="label">
                    Загрузить фото / видео
                    <input type="file" hidden multiple onChange={handleFileChange} />
                </Button>

                {previews.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1,
                            position: 'relative',
                            mt: 2,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Button
                                variant="outlined"
                                onClick={() => setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev))}
                                disabled={selectedIndex === 0}
                            >
                                ←
                            </Button>

                            <Box
                                sx={{
                                    position: 'relative',
                                    width: 300,
                                    height: 200,
                                    overflow: 'hidden',
                                    borderRadius: 2,
                                }}
                            >
                                <PreviewMedia
                                    src={previews[selectedIndex]}
                                    mimeType={files[selectedIndex]?.type}
                                    alt={`preview-${selectedIndex}`}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        display: 'block',
                                        border:
                                            selectedIndex === previewIndex
                                                ? '3px solid #1976d2'
                                                : '1px solid #ccc',
                                        borderRadius: 8,
                                    }}
                                />
                                <IconButton
                                    onClick={() => handleRemoveFile(selectedIndex)}
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        bgcolor: 'white',
                                        color: 'red',
                                        '&:hover': { bgcolor: '#ffe5e5' },
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>

                            <Button
                                variant="outlined"
                                onClick={() =>
                                    setSelectedIndex((prev) =>
                                        prev < previews.length - 1 ? prev + 1 : prev
                                    )
                                }
                                disabled={selectedIndex === previews.length - 1}
                            >
                                →
                            </Button>
                        </Box>

                        <Box display="flex" gap={2} alignItems="center">
                            <Typography variant="caption">
                                {selectedIndex + 1} / {previews.length}
                            </Typography>

                            <Button
                                variant={selectedIndex === previewIndex ? 'contained' : 'outlined'}
                                size="small"
                                onClick={() => setPreviewIndex(selectedIndex)}
                            >
                                {selectedIndex === previewIndex
                                    ? 'Главное изображение'
                                    : 'Сделать главным'}
                            </Button>
                        </Box>
                    </Box>
                )}


                <Button variant="contained" color="primary" onClick={handleSubmit}>
                    Создать диск
                </Button>
            </Box>
        </Box>
    );
};

export default CreateWheelPage;
